import React from 'react';
import styles from './index.module.scss';
import { formatContentHTML } from '../../../../../utils/formatBoldText';
import { formatCardFilterText } from '../../../../../utils/formatFilterText';
import AuthorAvatars from '../../../other_components/author_avatars';
import UnknownText from '../../../text_components/unknown_text';
import UnknownLongText from '../../../long_text_components/unknown_long_text';
import GalleryComponent from '../../../imgs_block_components/gallery_component';
import UnknownImgsBlock from '../../../imgs_block_components/unknown_imgs_block';
import VideoComponent from '../../../video_components/video_component';
import UnknownVideo from '../../../video_components/unknown_video';

const normalizeText = (value) => {
  const text = String(value ?? '').trim();
  if (!text || text.toLowerCase() === 'none') return '';
  return text;
};

const getImageFileName = (content) => {
  const rawName = normalizeText(content?.img_name);
  if (!rawName) return '';
  if (rawName.includes('\\')) return rawName.substring(rawName.lastIndexOf('\\') + 1);
  if (rawName.includes('/')) return rawName.split('/').pop();
  return rawName;
};

const PostBlock = ({
  blockData,
  path_to_folder,
  filterA,
  filterB,
  imgs_for_filters,
  authorAvatars = [],
}) => {
  const filterAImage = React.useMemo(() => {
    if (!filterA || !imgs_for_filters || imgs_for_filters.length === 0) return null;

    const filterAStr = Array.isArray(filterA) ? filterA.join(', ') : String(filterA);
    const matched = imgs_for_filters.find((item) => item.word === filterAStr);
    return matched ? matched.img : null;
  }, [filterA, imgs_for_filters]);

  const renderExtraContent = (content) => {
    if (content.cotent_type === 'text' || content.content_type === 'text') {
      return (
        <UnknownText
          contentText={content.content_text}
          contentTag={content.cotent_tag || content.content_tag}
          contentName={content.cotent_name || content.content_name}
        />
      );
    }

    if (content.cotent_type === 'long_text' || content.content_type === 'long_text') {
      return (
        <UnknownLongText
          contentText={content.content_text}
          contentTag={content.cotent_tag || content.content_tag}
          contentName={content.cotent_name || content.content_name}
        />
      );
    }

    if (content.cotent_type === 'imgs_block' || content.content_type === 'imgs_block') {
      switch (content.cotent_tag || content.content_tag) {
        case 'gallery':
          return (
            <GalleryComponent
              contentData={content}
              contentName={content.cotent_name || content.content_name}
              path_to_folder={path_to_folder}
            />
          );
        default:
          return (
            <UnknownImgsBlock
              contentData={content}
              contentName={content.cotent_name || content.content_name}
              path_to_folder={path_to_folder}
            />
          );
      }
    }

    if (content.cotent_type === 'video' || content.content_type === 'video') {
      switch (content.cotent_tag || content.content_tag) {
        case 'main_video':
          return (
            <VideoComponent
              contentData={content}
              contentName={content.cotent_name || content.content_name}
              path_to_folder={path_to_folder}
            />
          );
        default:
          return (
            <UnknownVideo
              contentData={content}
              contentName={content.cotent_name || content.content_name}
              path_to_folder={path_to_folder}
            />
          );
      }
    }

    return null;
  };

  if (!blockData?.block_content || !Array.isArray(blockData.block_content)) {
    return null;
  }

  const blockContent = blockData.block_content;

  const imageItems = blockContent.filter(
    (item) => (item?.cotent_type === 'img' || item?.content_type === 'img') && getImageFileName(item),
  );
  const coverImage = imageItems[0] || null;
  const extraImages = imageItems.slice(1);

  const titleContent = blockContent.find(
    (item) =>
      (item?.cotent_type === 'text' || item?.content_type === 'text') &&
      (item?.cotent_tag === 'title' || item?.content_tag === 'title') &&
      normalizeText(item?.content_text),
  );

  const descriptionContent = blockContent.find(
    (item) =>
      (item?.cotent_type === 'long_text' || item?.content_type === 'long_text') &&
      (item?.cotent_tag === 'description' || item?.content_tag === 'description') &&
      normalizeText(item?.content_text),
  );

  const imageDescriptionTexts = imageItems
    .map((item) => normalizeText(item?.content_text))
    .filter(Boolean);

  const extraContent = blockContent.filter((item) => {
    const type = item?.cotent_type || item?.content_type;
    const tag = item?.cotent_tag || item?.content_tag;

    if (type === 'img') return false;
    if (type === 'text' && tag === 'title') return false;
    if (type === 'long_text' && tag === 'description') return false;
    return true;
  });

  const imageSrc =
    coverImage && path_to_folder ? `${path_to_folder}${getImageFileName(coverImage)}` : '';

  const hasDescription = Boolean(descriptionContent) || imageDescriptionTexts.length > 0;
  const hasExtra = extraContent.length > 0 || extraImages.length > 0;

  return (
    <article className={styles.postBlock}>
      {imageSrc && (
        <div className={styles.coverWrap}>
          <img src={imageSrc} alt='' className={styles.coverImage} loading='lazy' draggable={false} />
        </div>
      )}

      <div className={styles.body}>
        {titleContent && (
          <div className={styles.titleSection}>
            <div className={styles.titleAndFiltersContainer}>
              <div className={styles.titleWrap}>
                <h3
                  className={styles.cardTitle}
                  dangerouslySetInnerHTML={{
                    __html: formatContentHTML(normalizeText(titleContent.content_text)),
                  }}
                />
              </div>
              {filterA && (
                <div className={styles.filtersContainer}>
                  <div className={styles.filterBadge}>
                    {filterAImage ? (
                      <img src={filterAImage} alt='filter' className={styles.filterImage} />
                    ) : (
                      <span className={styles.filterValue}>
                        {Array.isArray(filterA) ? filterA.join(', ') : String(filterA)}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {titleContent && hasDescription && <div className={styles.titleRule} aria-hidden='true' />}

        {descriptionContent && (
          <div className={styles.descriptionWrap}>
            <p
              className={styles.cardDescription}
              dangerouslySetInnerHTML={{
                __html: formatContentHTML(normalizeText(descriptionContent.content_text)),
              }}
            />
          </div>
        )}

        {!descriptionContent &&
          imageDescriptionTexts.map((text, index) => (
            <div key={`desc-${index}`} className={styles.descriptionWrap}>
              <p
                className={styles.cardDescription}
                dangerouslySetInnerHTML={{ __html: formatContentHTML(text) }}
              />
            </div>
          ))}

        {hasExtra && (
          <div className={styles.extraContent}>
            {extraImages.map((content, index) => {
              const src = `${path_to_folder}${getImageFileName(content)}`;
              return (
                <div key={`extra-img-${index}`} className={styles.extraImageWrap}>
                  <img src={src} alt='' className={styles.extraImage} loading='lazy' />
                </div>
              );
            })}
            {extraContent.map((content, index) => (
              <div key={`extra-${index}`} className={styles.extraItem}>
                {renderExtraContent(content)}
              </div>
            ))}
          </div>
        )}

        <div className={styles.cardFooter}>
          <AuthorAvatars authors={authorAvatars} variant='light' placement='footer' />
        </div>

        <div className={styles.bottomActionRow}>
          {filterB && filterB !== 'все' && (
            <span className={styles.filterBText}>{formatCardFilterText(filterB)}</span>
          )}
          <div className={styles.bottomUnderline} aria-hidden='true' />
        </div>
      </div>

    </article>
  );
};

export default PostBlock;
